import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import { adminUsuariosAPI } from '../../utils/adminUsuariosAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import ModalRodape from './ModalRodape';

const inputClasses = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all';
const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5';

// Só admin abre esse modal (o botão "Novo usuário" nem aparece pra
// gerenciador) — nunca precisa esconder o campo "Tipo" ou validar quem tá
// pedindo, a rota já garante isso (requireTipoAdmin).
const CriarUsuarioModal = ({ onFechar, onCriado }) => {
  const [enviando, setEnviando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { nomeCompleto: '', email: '', password: '', tipo: 'gerenciador' }
  });

  const onSubmit = async (dados) => {
    setEnviando(true);
    try {
      await adminUsuariosAPI.criar(dados);
      sucessoComHorario(`Usuário "${dados.nomeCompleto}" criado`);
      onCriado();
      onFechar();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao criar usuário');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="criar-usuario-nome" className={labelClasses}>Nome completo</label>
        <input id="criar-usuario-nome" {...register('nomeCompleto', { required: 'Obrigatório' })} className={inputClasses} autoFocus />
        {errors.nomeCompleto && <p className="text-sm text-red-600 mt-1">{errors.nomeCompleto.message}</p>}
      </div>

      <div>
        <label htmlFor="criar-usuario-email" className={labelClasses}>E-mail</label>
        <input
          id="criar-usuario-email"
          type="email"
          {...register('email', {
            required: 'Obrigatório',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Informe um e-mail válido' }
          })}
          className={inputClasses}
        />
        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="criar-usuario-senha" className={labelClasses}>Senha</label>
        <div className="relative">
          <input
            id="criar-usuario-senha"
            type={mostrarSenha ? 'text' : 'password'}
            {...register('password', { required: 'Obrigatório', minLength: { value: 8, message: 'Mínimo 8 caracteres' } })}
            className={`${inputClasses} pr-10`}
          />
          <button
            type="button"
            onClick={() => setMostrarSenha((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
            aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>}
      </div>

      <div>
        <label htmlFor="criar-usuario-tipo" className={labelClasses}>Tipo</label>
        <select id="criar-usuario-tipo" {...register('tipo')} className={inputClasses}>
          <option value="gerenciador">Gerenciador</option>
          <option value="admin">Administrador</option>
        </select>
      </div>

      <ModalRodape>
        <button type="submit" disabled={enviando} className="btn-primary">
          {enviando ? 'Criando...' : 'Criar usuário'}
        </button>
      </ModalRodape>
    </form>
  );
};

export default CriarUsuarioModal;
